/**
 * Componente que renderiza Brand Emojis dinamicamente do Supabase Storage
 * 
 * Usa contexto semântico para selecionar o emoji apropriado,
 * com fallback para Unicode quando o asset não está disponível.
 */
import { memo } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useBrandEmoji, type EmojiContext } from "@/hooks/useBrandEmoji";
import { getBrandAssetUrl, type BrandAsset } from "@/hooks/useBrandAssets";

// Tamanhos predefinidos
const SIZES = {
  xs: { container: "w-4 h-4", fontSize: "text-xs", imgSize: 16 },
  sm: { container: "w-6 h-6", fontSize: "text-sm", imgSize: 24 },
  md: { container: "w-8 h-8", fontSize: "text-lg", imgSize: 32 },
  lg: { container: "w-12 h-12", fontSize: "text-2xl", imgSize: 48 },
  xl: { container: "w-16 h-16", fontSize: "text-4xl", imgSize: 64 },
  "2xl": { container: "w-20 h-20", fontSize: "text-5xl", imgSize: 80 },
} as const;

type EmojiSize = keyof typeof SIZES;

interface DynamicBrandEmojiProps {
  /** Contexto semântico para seleção automática do emoji */
  context?: EmojiContext;
  /** Ou asset_id direto do banco de dados */
  assetId?: string;
  /** Asset pré-carregado (para evitar nova query) */
  asset?: BrandAsset;
  /** Tamanho do emoji */
  size?: EmojiSize;
  /** Classes CSS adicionais */
  className?: string;
  /** Desabilitar animação */
  static?: boolean;
  /** Texto alternativo */
  alt?: string;
}

/**
 * Renderiza um Brand Emoji do Supabase Storage
 * 
 * @example
 * // Por contexto semântico
 * <DynamicBrandEmoji context="success" size="lg" />
 * 
 * // Por asset_id específico
 * <DynamicBrandEmoji assetId="05-elementos-gra-ficos-legal-amarelo" />
 * 
 * // Com asset pré-carregado
 * <DynamicBrandEmoji asset={achievementAsset} size="xl" />
 */
export const DynamicBrandEmoji = memo(function DynamicBrandEmoji({
  context,
  assetId,
  asset: preloadedAsset,
  size = "md",
  className,
  static: isStatic = false,
  alt,
}: DynamicBrandEmojiProps) {
  const sizeConfig = SIZES[size];
  
  // Se temos asset pré-carregado, não precisa de query
  const shouldFetch = !preloadedAsset && !!context;
  const { url: contextUrl, fallbackEmoji, isLoading } = useBrandEmoji(
    context || "info",
    { width: sizeConfig.imgSize * 2, height: sizeConfig.imgSize * 2 }
  );

  // Determinar URL final
  let imageUrl: string | null = null;
  
  if (preloadedAsset) {
    imageUrl = getBrandAssetUrl(preloadedAsset.storage_path, {
      width: sizeConfig.imgSize * 2,
      height: sizeConfig.imgSize * 2,
    });
  } else if (assetId) {
    // Para asset_id direto, construímos a URL diretamente
    // Isso assume que o storage_path segue o padrão do asset_id
    imageUrl = null; // Será handled pelo fallback
  } else if (contextUrl) {
    imageUrl = contextUrl;
  }

  // Animação sutil de hover/pulse
  const motionProps = isStatic ? {} : {
    whileHover: { scale: 1.1 },
    whileTap: { scale: 0.95 },
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { type: "spring" as const, stiffness: 400, damping: 17 },
  };

  // Container base classes
  const containerClasses = cn(
    "inline-flex items-center justify-center select-none",
    sizeConfig.container,
    className
  );

  // Fallback para Unicode enquanto carrega ou se não houver URL
  if (isLoading || !imageUrl) {
    return (
      <motion.span 
        className={cn(containerClasses, sizeConfig.fontSize)}
        {...(isStatic ? {} : motionProps)}
        aria-label={alt || context || "emoji"}
      >
        {fallbackEmoji}
      </motion.span>
    );
  }

  // Renderiza imagem do Storage
  return (
    <motion.img
      src={imageUrl}
      alt={alt || preloadedAsset?.display_name || context || "Auli emoji"}
      className={cn(containerClasses, "object-contain")}
      loading="lazy"
      {...motionProps}
      onError={(e) => {
        // Fallback para Unicode se imagem falhar
        const target = e.currentTarget;
        const span = document.createElement("span");
        span.className = cn(containerClasses, sizeConfig.fontSize);
        span.textContent = fallbackEmoji;
        target.parentNode?.replaceChild(span, target);
      }}
    />
  );
});

/**
 * Versão simplificada para uso inline em texto
 */
export const InlineBrandEmoji = memo(function InlineBrandEmoji({
  context,
  className,
}: {
  context: EmojiContext;
  className?: string;
}) {
  return (
    <DynamicBrandEmoji 
      context={context} 
      size="sm" 
      static 
      className={cn("inline align-middle mx-0.5", className)} 
    />
  );
});

export default DynamicBrandEmoji;
